'use client'

import Image from 'next/image'
import { useFeatureIsOn } from 'growthbook'
import { Button } from '@repo/design-system-cf/components/button'
import { ChatGptIcon } from '@repo/studio-design-system-ui-icons/ai-model-logos/chat-gpt'
import { getGptImageGalleryImages } from '@repo/studio-components-gpt-image-banner/gallery-images'
import { ShowcaseGrid } from '@repo/studio-components-showcase-grid/showcase-grid'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { LFS_URL } from '@repo/web-utils/env'
import { FEATURE_GROW_3418_GPT_IMAGE_2_OPEN_HOVER } from '@web/utils/features'

// The ticket pins the flow deep-link to the numeric GPT Image 2 model id (`?model=7`),
// which differs from the carousel banner's `?model=OpenAigptImage2` slug.
const GPT_IMAGE_2_FLOW_URL = `${AppRoutes.STUDIO_TOOL_FLOW}?model=7`

const SUPPLEMENTARY_IMAGE_URLS = [
  `${LFS_URL}/studio/homepage/gpt-gallery/extra/1_254x450.webp`,
  `${LFS_URL}/studio/homepage/gpt-gallery/extra/2_450x450.webp`,
  `${LFS_URL}/studio/homepage/gpt-gallery/extra/3_450x450.webp`,
  `${LFS_URL}/studio/homepage/gpt-gallery/extra/4_450x450.webp`,
  `${LFS_URL}/studio/homepage/gpt-gallery/extra/5_339x450.webp`,
  `${LFS_URL}/studio/homepage/gpt-gallery/extra/6_450x450.webp`,
]

const GALLERY_IMAGES = [
  ...getGptImageGalleryImages(LFS_URL ?? ''),
  ...SUPPLEMENTARY_IMAGE_URLS.map(src => ({ src })),
]

interface GptImage2GalleryBlockProps {
  title: string
  subtitle: string
  ctaLabel: string
  openLabel: string
  className?: string
}

export function GptImage2GalleryBlock({
  title,
  subtitle,
  ctaLabel,
  openLabel,
  className = '',
}: GptImage2GalleryBlockProps) {
  // GROW-3418: 50/50 test of a black hover scrim reading "Open" on each tile. GrowthBook targets
  // only users without an active Studio AI trial or subscription, so the flag alone doesn't gate
  // the audience — the experiment's targeting condition does.
  const isOpenHoverOn = useFeatureIsOn(FEATURE_GROW_3418_GPT_IMAGE_2_OPEN_HOVER)

  return (
    <ShowcaseGrid.Root className={`bg-cf-white ${className}`}>
      <ShowcaseGrid.Header
        icon={
          // eslint-disable-next-line cf-web/require-icon-parent
          <ChatGptIcon className="size-5 text-cf-black" />
        }
        title={title}
        titleClassName="font-poppins text-xl lg:text-2xl font-bold text-cf-black"
        subtitle={subtitle}
        subtitleClassName="text-sm text-cf-grey-500"
      />
      <ShowcaseGrid.Body>
        <ShowcaseGrid.Grid>
          {GALLERY_IMAGES.map((image, index) => (
            <ShowcaseGrid.Tile key={`${image.src}-${index}`} index={index}>
              <a
                href={GPT_IMAGE_2_FLOW_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ctaLabel}
                className="group relative block h-full w-full overflow-hidden rounded-sm border border-cf-sf-gray-350/40 bg-cf-grey-200 focus-visible:ring-2 focus-visible:ring-cf-green focus-visible:outline-none"
              >
                <Image
                  src={image.src}
                  alt=""
                  fill
                  unoptimized
                  sizes="(min-width: 768px) 20vw, (min-width: 640px) 25vw, 33vw"
                  className="object-cover transition-transform duration-200 group-hover:scale-105"
                />
                {isOpenHoverOn ? (
                  <div className="absolute inset-0 hidden items-center justify-center bg-cf-black/60 text-lg font-medium text-cf-white group-hover:flex group-focus-visible:flex">
                    {openLabel}
                  </div>
                ) : null}
              </a>
            </ShowcaseGrid.Tile>
          ))}
        </ShowcaseGrid.Grid>
        <ShowcaseGrid.Cta scrimClassName="bg-gradient-to-t from-cf-white via-cf-white/85 to-transparent">
          <Button
            variant="primary"
            size="regular"
            asChild
            className="absolute bottom-2 left-1/2 -translate-x-1/2 gap-1 shadow-lg"
          >
            <a href={GPT_IMAGE_2_FLOW_URL} target="_blank" rel="noopener noreferrer">
              {/* eslint-disable-next-line cf-web/require-icon-parent */}
              <ChatGptIcon className="!size-4" />
              <span>{ctaLabel}</span>
            </a>
          </Button>
        </ShowcaseGrid.Cta>
      </ShowcaseGrid.Body>
    </ShowcaseGrid.Root>
  )
}
